/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { AbstractObject, uuid } from '@src/app/model/abstract-object';

/**
 * One answer from the AI, in whatever shape the prompt that produced it uses. These are the fields
 * of a request qualification; an invoice extraction will carry its own.
 */
export interface AiAnswer {
    srTopic?: string;
    srClass?: string;
    srArea?: uuid;
    srCore?: uuid;
    srStatus?: string;
    responseType?: string;
    comment?: string;
    /** Only ever present as false - the reviewer saved the answer without sending the reply. */
    replySent?: boolean;
    /** Avoid erroring if unexpected attribute */
    [field: string]: unknown;
}

/** What a person did with an answer - see AiLog on the server, which writes these. */
export const AI_OUTCOMES: AbstractObject[] = [
    { id: 'auto', name: $localize`Sent automatically` },
    { id: 'pending', name: $localize`Waiting for review` },
    { id: 'accepted', name: $localize`Accepted as written` },
    { id: 'changed', name: $localize`Changed by reviewer` },
    { id: 'rejected', name: $localize`Rejected` },
];

export class AiLog extends AbstractObject {
    static OUTCOME_AUTO = 'auto';
    static OUTCOME_PENDING = 'pending';
    static OUTCOME_ACCEPTED = 'accepted';
    static OUTCOME_CHANGED = 'changed';
    static OUTCOME_REJECTED = 'rejected';

    type: string;
    agentTeamId: uuid;
    relatedId: uuid;
    relatedName: string;
    month: string;

    createdById: uuid;
    createdByName: string;
    requesterEmail: string;

    aiModel: string;
    promptHash: string;
    contextHash: string;
    question: string;

    proposed: AiAnswer;
    final: AiAnswer;

    /** The proposed comment with its line breaks as markup - the editor needs it that way. */
    commentHtml: string;

    inTokens: number;
    outTokens: number;
    cacheWriteTokens: number;
    cacheReadTokens: number;
    costUsd: number;
    durationMs: number;

    failed: number;
    errorMessage: string;

    outcome: string;
    /** Percentage of the proposed comment still there in what was sent - 100 is untouched. */
    commentMatch: number;
    fieldsChanged: number;
    reviewedAt: number;
    reviewedById: uuid;
    reviewedByName: string;

    constructor(o: Partial<AiLog> = {}) {
        super(o);
    }

    private static labels: Record<string, string> = {
        srTopic: $localize`Topic`,
        srClass: $localize`Class`,
        srArea: $localize`Area`,
        srCore: $localize`Core`,
        srStatus: $localize`Status`,
    };

    private static labelFor(field: string): string {
        return AiLog.labels[field] ?? field;
    }

    static reviewSummary(log: AiLog): string {
        if (!log?.final) {
            return '';
        }
        const correct: string[] = [];
        const corrected: string[] = [];
        for (const field of Object.keys(log.final)) {
            const aiValue = AiLog.valueOf(log.proposed, field);
            const humanValue = AiLog.valueOf(log.final, field);
            if (field === 'comment' || field === 'replySent' || (aiValue === '' && humanValue === '')) {
                continue;
            }
            if (aiValue === humanValue) {
                correct.push(AiLog.labelFor(field));
            } else {
                corrected.push($localize`${AiLog.labelFor(field)} ${AiLog.quoted(aiValue)} set to ${AiLog.quoted(humanValue)}`);
            }
        }
        const summary = [AiLog.commentSummary(log)];
        if (correct.length > 0) {
            summary.push($localize`${correct.join(', ')} correctly assigned`);
        }
        return [...summary, ...corrected].join(', ');
    }

    private static commentSummary(log: AiLog): string {
        return log.final.replySent === false
            ? $localize`Reply not sent`
            : $localize`${Math.round(log.commentMatch)}% text reused`;
    }

    private static valueOf(answer: AiAnswer, field: string): string {
        return (answer?.[field] ?? '').toString();
    }

    private static quoted(value: string): string {
        return value === '' ? $localize`(none)` : `"${value}"`;
    }
}
